// src/components/support/ClientSupportPage.jsx
//
// Client "Support" page (/support): open a ticket, follow every ticket on the
// account, reply, and confirm when something is fixed. Everyone on the client
// account sees the account's tickets (a fleet team shares the problem).
// ?ticket=<id> opens one directly - push notifications and emails link there.
//
// Who sees it: client roles in SUPPORT_CLIENT_ROLES on the server (default all).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../../api/apiClient';
import FleetHeader from '../FleetHeader';
import { LoadingScreen } from '../billing/billingShared';
import NewTicketSheet from './NewTicketSheet';
import TicketDrawer from './TicketDrawer';
import { PriorityTag, TicketStatus, timeAgo } from './supportShared';
import '../../styles/fleet-theme.css';
import '../billing/Billing.css';
import './Support.css';

const FILTERS = [
  { id: 'open', label: 'Open', match: (t) => t.is_open || t.status === 'resolved' },
  { id: 'closed', label: 'Closed', match: (t) => t.status === 'closed' },
];

export default function ClientSupportPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('open');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState(null);

  const openId = Number(new URLSearchParams(location.search).get('ticket')) || null;
  const openTicket = (id) => navigate(id ? `/support?ticket=${id}` : '/support', { replace: !id });

  const load = useCallback(async () => {
    try {
      const d = await apiFetchJSON('/api/v2/client/support/tickets');
      setTickets(d.tickets);
      setError(null);
    } catch (e) {
      setError(e.status === 403 ? (e.message || 'Support isn’t available for your account.') : (e.message || 'Could not load tickets'));
      setTickets((t) => t || []);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => document.visibilityState === 'visible' && load(), 60000);
    return () => clearInterval(id);
  }, [load]);

  const rows = useMemo(() => (tickets || []).filter(FILTERS.find((f) => f.id === filter).match), [tickets, filter]);
  const waiting = (tickets || []).filter((t) => t.status === 'waiting_client');
  const resolved = (tickets || []).filter((t) => t.status === 'resolved');
  const openCount = (tickets || []).filter((t) => t.is_open).length;

  if (!tickets) return <LoadingScreen text="Loading support…" />;

  return (
    <div>
      <FleetHeader title="Support" user={user} onLogout={onLogout} scope="client" />
      <div className="sd-page bl-page sp-page">
        {error && <div className="ft-toolbar-error" role="alert">{error}</div>}
        {toast && (
          <div className="bl-toast" role="status">
            {toast}
            <button type="button" className="bl-icon-btn" onClick={() => setToast(null)} aria-label="Dismiss"><i className="fas fa-times"></i></button>
          </div>
        )}

        <section className={`sp-hero ${waiting.length ? 'needs-you' : ''}`}>
          <div className="sp-hero-text">
            {waiting.length > 0 ? (
              <>
                <h1 className="sp-hero-title">{waiting.length === 1 ? 'A ticket needs your reply' : `${waiting.length} tickets need your reply`}</h1>
                <p className="sp-hero-note">Our team is waiting on information from you before they can continue.</p>
              </>
            ) : (
              <>
                <h1 className="sp-hero-title">{openCount ? `${openCount} open ${openCount === 1 ? 'ticket' : 'tickets'}` : 'How can we help?'}</h1>
                <p className="sp-hero-note">
                  {openCount
                    ? 'You’ll get a notification when there’s news. Everyone on your account can follow these tickets.'
                    : 'Tell us what’s wrong and which vessel. We see the vessel’s live status as soon as you send it.'}
                </p>
              </>
            )}
            {resolved.length > 0 && !waiting.length && (
              <p className="sp-hero-note">{resolved.length === 1 ? 'One ticket is' : `${resolved.length} tickets are`} marked resolved. Let us know if it’s working.</p>
            )}
          </div>
          <div className="sp-hero-actions">
            {waiting.length > 0 && (
              <button type="button" className="bl-btn-secondary" onClick={() => openTicket(waiting[0].id)}>Reply to {waiting[0].ref}</button>
            )}
            <button type="button" className="bl-btn-primary" onClick={() => setCreating(true)}>
              <i className="fas fa-plus"></i> Open a ticket
            </button>
          </div>
        </section>

        <div className="ft-filter-row bl-filters">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" className={'ft-pill' + (filter === f.id ? ' active' : '')} onClick={() => setFilter(f.id)}>
              {f.label}<span className="bl-pill-count">{tickets.filter(f.match).length}</span>
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="bl-empty">
            {filter === 'open' ? 'No open tickets. If something isn’t working, open a ticket and we’ll pick it up.' : 'No closed tickets yet.'}
          </div>
        ) : (
          <ul className="sp-list">
            {rows.map((t) => (
              <li key={t.id}>
                <button type="button" className={`sp-row ${t.unread ? 'is-unread' : ''} ${t.status === 'waiting_client' ? 'needs-you' : ''}`}
                        onClick={() => openTicket(t.id)}>
                  <span className="sp-row-main">
                    <span className="sp-row-subject">
                      {t.unread && <span className="sp-unread-dot" aria-label="New activity" />}
                      {t.subject}
                    </span>
                    <span className="sp-row-meta">
                      <span className="ft-mono">{t.ref}</span>
                      {t.site_name ? ` · ${t.site_name}` : ''} · {t.category_label}
                      {t.assigned_to_name && t.is_open ? ` · ${t.assigned_to_name}` : ''}
                    </span>
                  </span>
                  <span className="sp-row-side">
                    <span className="sp-row-tags"><PriorityTag priority={t.priority} /><TicketStatus status={t.status} scope="client" /></span>
                    <span className="sp-row-time">{timeAgo(t.last_activity_at)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && (
        <NewTicketSheet
          onClose={() => setCreating(false)}
          onCreated={(t) => {
            setCreating(false);
            setFilter('open');
            setToast(`Ticket ${t.ref} sent. We’ve alerted the team.`);
            load();
            openTicket(t.id);
          }}
        />
      )}

      {openId && (
        <TicketDrawer scope="client" ticketId={openId} onClose={() => { openTicket(null); load(); }} onChanged={() => load()} />
      )}
    </div>
  );
}
